import {useDraggable} from '@dnd-kit/core';
import {css} from 'styled-components';

import {ReactNode} from 'react';

export interface VehicleAlbumDragPayload {
  sourceAlbumId: string;
  previewUrl: string;
  // Optional, and deliberately outside the identity guard: a missing count costs a badge, not the drop.
  photoCount?: number;
  moveTo: (destinationAlbumId: string) => void;
}

interface VehicleAlbumDraggablePhotoProps {
  photoId: string;
  sourceAlbumId: string;
  previewUrl: string;
  // 1 outside Bulk Selection Mode; inside it the whole Selection rides on every selected tile.
  photoCount: number;
  isDisabled?: boolean;
  onMove: (destinationAlbumId: string, photoId: string) => void;
  children: ReactNode;
}

// The source tile stays mounted during the async move; dimming reads as a move, not a copy.
const DRAGGED_TILE_OPACITY = 0.4;

// Only while draggable: Clickable sets its own cursor on tiles that still open the lightbox,
// and inheriting from a non-draggable wrapper would strip their pointer.
const DRAGGABLE_CURSOR = css`
  cursor: grab;

  & * {
    cursor: inherit;
  }
`;

export function VehicleAlbumDraggablePhoto(props: VehicleAlbumDraggablePhotoProps) {
  // dnd-kit keeps data in a ref, so a closure is safe here.
  const payload: VehicleAlbumDragPayload = {
    sourceAlbumId: props.sourceAlbumId,
    previewUrl: props.previewUrl,
    photoCount: props.photoCount,
    moveTo: (destinationAlbumId) => props.onMove(destinationAlbumId, props.photoId),
  };

  const {setNodeRef, listeners, attributes, isDragging} = useDraggable({
    id: props.photoId,
    disabled: props.isDisabled,
    data: payload,
  });

  // Only this one attribute, and only while draggable. It is dnd-kit's sole link from a tile to
  // the DndContext instructions, so without it that text is in the DOM but announced to nobody.
  // The rest of `attributes` is deliberately dropped: role="button" and tabIndex would make the
  // tile focusable with no KeyboardSensor to serve it, and aria-roledescription defaults to the
  // untranslated literal "draggable".
  const describedBy = props.isDisabled ? undefined : attributes['aria-describedby'];

  return (
    // Listeners go here, not on VehicleAlbumImage: it filters props through a fixed allowlist.
    <div
      ref={setNodeRef}
      {...listeners}
      aria-describedby={describedBy}
      css={css`
        opacity: ${isDragging ? DRAGGED_TILE_OPACITY : 1};
        ${props.isDisabled ? null : DRAGGABLE_CURSOR}
      `}
    >
      {props.children}
    </div>
  );
}
